import * as React from 'react';

import { useLocation, useParams, useRoutes } from 'js/lib/useRouter';

import type { UniversityProgramDeadlines } from 'bundles/degree-description/__generated__/baseContentfulTypes';
import CareerOutcomesModule from 'bundles/degree-description/components/modules/career-outcomes/CareerOutcomesModule';
import CareerModule from 'bundles/degree-description/components/modules/career-section/CareerModule';
import EoiInlineModule from 'bundles/degree-description/components/modules/expression-of-interest/EoiInlineModule';
import AtAGlanceHorizontalBlock from 'bundles/degree-description/components/modules/hero/AtAGlanceHorizontalBlock';
import DeadlinesBlock from 'bundles/degree-description/components/modules/hero/DeadlinesBlock';
import EoiButtonsBlock from 'bundles/degree-description/components/modules/hero/EoiButtonsBlock';
import HeroBanner from 'bundles/degree-description/components/modules/hero/HeroBanner';
import HeroModule from 'bundles/degree-description/components/modules/hero/HeroModule';
import InformationalSectionContainer from 'bundles/degree-description/components/modules/hero/InformationalSectionContainer';
import InfoSectionModule from 'bundles/degree-description/components/modules/info-section/InfoSectionModule';
import PersonalizedEmbeddedMsg from 'bundles/degree-description/components/modules/personalization/PersonalizedEmbeddedMsg';
import SchemaMarkup from 'bundles/degree-description/components/modules/structured-data-markup/SchemaMarkup';
import { HideOnMobile } from 'bundles/degree-description/components/shared/BreakpointDisplay';
import ModuleContainerStyleWrap from 'bundles/degree-description/components/shared/ModuleContainerStyleWrap';
import InlineNavigation from 'bundles/degree-description/components/shared/Navigation/InlineNavigation';
import RichText from 'bundles/degree-description/components/shared/RichText';
import { useDegreeDescriptionPageData } from 'bundles/degree-description/contexts/DegreeDescriptionPageContext';
import type { PropsFromDegreePageContent } from 'bundles/degree-description/pages/consumer/degree-description-page/DegreePageContent';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import { getMessagesToDisplay } from 'bundles/degree-description/utils/personalizedMessageCriteria';
import { PAGE_NAMES_MARKUP } from 'bundles/degree-description/utils/schema-markup';
import { getNavigationItems } from 'bundles/degree-description/utils/utils';
import LazyImgix from 'bundles/page/components/shared/LazyImgix';

const CareersPageContent = ({
  pageContent,
  eoiButtons,
  eoiContainerText,
  setEoiTrackingIndex,
  personalizedDdpData,
}: PropsFromDegreePageContent) => {
  const {
    careersAbout,
    backgroundImage,
    partner,
    programName,
    careersValueProp,
    atAGlance,
    careerSection,
    deadlineSectionCollection,
    redesign2026Modules,
  } = pageContent;

  const { experimentValues } = useDegreeDescriptionPageData();

  const showCareerOutcomesModule = Boolean(
    experimentValues?.redesign2026Variant &&
    experimentValues.redesign2026Variant !== 'control' &&
    redesign2026Modules?.careerOutcomes
  );

  // Page Sections
  const valuePropText = careersValueProp?.json;
  const valuePropImage = careersValueProp?.links?.assets?.block?.[0];

  const location = useLocation();
  const { slug } = useParams();
  const routes = useRoutes();

  // Page Section Tracking
  const { useGetViewPageSectionRef } = useDegreeDescriptionEventingContext();
  const pageHeaderTrackingRef = useGetViewPageSectionRef('page_header');
  const pageBodyTrackingRef = useGetViewPageSectionRef('page_body');

  // Personalization
  const messagesToDisplay = getMessagesToDisplay(
    personalizedDdpData,
    slug,
    programName,
    partner?.partnerName,
    deadlineSectionCollection?.items as UniversityProgramDeadlines[]
  );

  return (
    <div>
      <SchemaMarkup pageName={PAGE_NAMES_MARKUP.careers} pathName={location.pathname} pageContent={pageContent} />
      <HeroModule
        informationalSection={
          <InformationalSectionContainer
            content={
              careersAbout?.json && (
                <div ref={pageHeaderTrackingRef}>
                  <RichText
                    content={careersAbout?.json}
                    contentLinks={careersAbout?.links}
                    linkTrackingName="careersAbout_body_link"
                    buttonTrackingNameV3="degree_info_link"
                    trackingSection="page_header"
                  />
                </div>
              )
            }
            sidebarContent={
              <HideOnMobile>
                <DeadlinesBlock finalDeadlineInfo={atAGlance?.finalDeadlineInfo} />
              </HideOnMobile>
            }
          />
        }
        heroBanner={
          <HeroBanner
            backgroundImageUrl={backgroundImage?.url ?? ''}
            partnerLogoUrl={partner?.partnerLogo?.url ?? ''}
            partnerName={partner?.partnerName ?? ''}
            programName={programName ?? ''}
            sidebarContent={
              <EoiButtonsBlock
                eoiContainerText={eoiContainerText}
                eoiButtons={eoiButtons}
                trackingIndex={1}
                setEoiTrackingIndex={setEoiTrackingIndex}
              />
            }
          />
        }
        contentInBetween={
          <>
            <AtAGlanceHorizontalBlock atAGlance={atAGlance} />
            <InlineNavigation
              navigationItems={getNavigationItems()}
              // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              parentNavRoute={routes[routes.length - 2].name}
              // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              currentNavRoute={routes[routes.length - 1].name}
              // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              slug={slug}
            />
          </>
        }
      />
      <div ref={pageBodyTrackingRef}>
        {messagesToDisplay.embeddedMessageToDisplay && (
          <ModuleContainerStyleWrap blueBackground>
            <PersonalizedEmbeddedMsg
              personalizedMessage={messagesToDisplay.embeddedMessageToDisplay}
              eoiButtons={eoiButtons}
              setEoiTrackingIndex={setEoiTrackingIndex}
            />
          </ModuleContainerStyleWrap>
        )}

        {showCareerOutcomesModule && (
          <ModuleContainerStyleWrap>
            <CareerOutcomesModule
              heading={redesign2026Modules?.careerOutcomes?.heading}
              valueProps={redesign2026Modules?.careerOutcomes?.valuePropsCollection?.items}
              rolesTitle={redesign2026Modules?.careerOutcomes?.rolesHeading}
              roles={redesign2026Modules?.careerOutcomes?.rolesCollection?.items}
              logosTitle={redesign2026Modules?.careerOutcomes?.logosHeading}
              logos={redesign2026Modules?.careerOutcomes?.whereGraduatesWorkLogosCollection?.items}
            />
          </ModuleContainerStyleWrap>
        )}

        {careerSection && !showCareerOutcomesModule && (
          <ModuleContainerStyleWrap>
            <CareerModule careerSection={pageContent.careerSection} trackingSection="page_body" />
          </ModuleContainerStyleWrap>
        )}
        {valuePropText && valuePropImage && valuePropImage?.url && valuePropImage?.width && valuePropImage?.height && (
          <ModuleContainerStyleWrap>
            <InfoSectionModule
              description={
                <RichText
                  content={valuePropText}
                  linkTrackingName="careersValueProp_info_link"
                  buttonTrackingNameV3="degree_info_link"
                  trackingSection="value_proposition"
                />
              }
              media={
                <LazyImgix
                  src={valuePropImage.url}
                  maxWidth={valuePropImage.width}
                  maxHeight={valuePropImage.height}
                  alt={valuePropImage.description || ''}
                  enableFluidWidth={true}
                />
              }
            />
          </ModuleContainerStyleWrap>
        )}
        <HideOnMobile>
          <EoiInlineModule
            eoiContainerText={eoiContainerText}
            eoiButtons={eoiButtons}
            trackingIndex={2}
            setEoiTrackingIndex={setEoiTrackingIndex}
          />
        </HideOnMobile>
      </div>
    </div>
  );
};

export default CareersPageContent;
